import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Users, Calendar, Ticket, TrendingUp, Trash2, Shield, AlertTriangle, CheckCircle, XCircle, Clock, Eye, Image, ShieldCheck, ShieldOff, Lock, UserCog, Headphones, DollarSign, Activity, Search, RefreshCw, Cloud, BarChart2, Mail, RotateCcw, ScanLine, ExternalLink, Copy } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserActivityView from '../../components/admin/UserActivityView';
import SecurityLog from '../../components/admin/SecurityLog';
import TicketResendManager from '../../components/admin/TicketResendManager';
import AdminPayouts from '../../components/admin/AdminPayouts';
import AdminDiagnostics from '../../components/admin/AdminDiagnostics';
import AdminHealthBanner from '../../components/admin/AdminHealthBanner';
import BankAccountRequests from '../../components/admin/BankAccountRequests';
import { getImageUrl } from '../../utils/apiUrl';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ADMIN_ROLE_LABELS = {
  super_admin: { label: 'Super Admin', color: 'bg-red-500' },
  support: { label: 'Support', color: 'bg-blue-500' },
  finance: { label: 'Finance', color: 'bg-green-500' },
  moderator: { label: 'Moderator', color: 'bg-yellow-500' },
};

export default function AdminPanel() {
  const { user, has2FAEnabled } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 1, limit: 50, total_count: 0, total_pages: 0 });
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });
  const [approvalDialog, setApprovalDialog] = useState({ open: false, event: null, action: '' });
  const [rejectionReason, setRejectionReason] = useState('');
  const [accessError, setAccessError] = useState(null);
  
  // User Activity State
  const [activityViewOpen, setActivityViewOpen] = useState(false);
  const [activityUser, setActivityUser] = useState(null);
  const [usersWithActivity, setUsersWithActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  // Controlled tab state so the Health banner can jump to a specific tab + subtab
  const [activeTab, setActiveTab] = useState('pending');
  const [diagSubtab, setDiagSubtab] = useState('trace');

  const jumpToTab = (tab, subtab) => {
    setActiveTab(tab);
    if (tab === 'diagnostics' && subtab) setDiagSubtab(subtab);
    // Scroll to the tabs row
    try { window.scrollTo({ top: 200, behavior: 'smooth' }); } catch {}
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch users with activity summary for super_admin
  useEffect(() => {
    if (user?.admin_role === 'super_admin') {
      fetchUsersWithActivity();
    }
  }, [user?.admin_role]);

  const fetchUsersWithActivity = async () => {
    setActivityLoading(true);
    try {
      const params = { limit: 50 };
      if (userSearchQuery) params.search = userSearchQuery;
      if (userRoleFilter && userRoleFilter !== 'all') params.role = userRoleFilter;
      
      const response = await axios.get(`${API_URL}/api/admin/users-with-activity-summary`, {
        params,
        
      });
      setUsersWithActivity(response.data);
    } catch (error) {
      console.error('Error fetching users with activity:', error);
      // Silently fail - this is an enhancement feature
    } finally {
      setActivityLoading(false);
    }
  };

  const handleViewUserActivity = (targetUser) => {
    setActivityUser(targetUser);
    setActivityViewOpen(true);
  };

  const fetchData = async (usersPage = usersPagination.page) => {
    setLoading(true);
    setAccessError(null);
    try {
      const [statsRes, usersRes, eventsRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users?page=${usersPage}&limit=50`),
        axios.get(`${API_URL}/api/admin/events`),
        axios.get(`${API_URL}/api/admin/events/pending`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setUsersPagination({
        page: usersRes.data.page,
        limit: usersRes.data.limit,
        total_count: usersRes.data.total_count,
        total_pages: usersRes.data.total_pages,
      });
      setEvents(eventsRes.data);
      setPendingEvents(pendingRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        setAccessError(error.response?.data?.detail || '2FA verification required for admin access');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role?role=${newRole}`, {});
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const handleDelete = async () => {
    const { type, item } = deleteDialog;
    try {
      if (type === 'user') {
        await axios.delete(`${API_URL}/api/admin/users/${item.user_id}`);
        toast.success('User deleted');
      } else if (type === 'event') {
        await axios.delete(`${API_URL}/api/events/${item.event_id}`);
        toast.success('Event deleted');
      }
      setDeleteDialog({ open: false, type: '', item: null });
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleApproval = async () => {
    const { event, action } = approvalDialog;
    try {
      await axios.put(
        `${API_URL}/api/admin/events/${event.event_id}/approve`,
        {
          status: action,
          rejection_reason: action === 'rejected' ? rejectionReason : null
        },
        
      );
      toast.success(`Event ${action === 'approved' ? 'approved' : 'rejected'} successfully`);
      setApprovalDialog({ open: false, event: null, action: '' });
      setRejectionReason('');
      fetchData();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const [reconcilingEvent, setReconcilingEvent] = useState(null);
  const [reinstateDialog, setReinstateDialog] = useState({ open: false, event: null });
  const [reinstateDate, setReinstateDate] = useState('');
  const [reinstateEndDate, setReinstateEndDate] = useState('');
  const [reinstating, setReinstating] = useState(false);
  // Tracks per-event scan-key generation so we never build a URL with `undefined`
  // and don't fire duplicate POSTs while one is in flight.
  const [scanKeyBusy, setScanKeyBusy] = useState(null);

  /**
   * Returns the event's scan_key, generating one on-demand if missing.
   * Always returns a non-empty string or null on failure (caller handles toast).
   * Side effect: updates the matching event in `data.all_events` with the new key
   * so subsequent renders don't re-call the API.
   */
  const ensureScanKey = async (event) => {
    if (!event?.event_id) return null;
    if (event.scan_key) return event.scan_key;
    try {
      setScanKeyBusy(event.event_id);
      const res = await axios.post(`${API_URL}/api/tickets/generate-scan-key`, { event_id: event.event_id });
      const newKey = res.data?.scan_key;
      if (!newKey) throw new Error('No scan_key returned');
      // Mutate in-memory event so subsequent clicks bypass the API
      event.scan_key = newKey;
      // Also patch the list state so any further render uses the new key
      setEvents(prev => (prev || []).map(e =>
        e.event_id === event.event_id ? { ...e, scan_key: newKey } : e
      ));
      return newKey;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate scan link');
      return null;
    } finally {
      setScanKeyBusy(null);
    }
  };

  const handleCopyScanLink = async (event) => {
    const key = await ensureScanKey(event);
    if (!key) return;
    const url = `${window.location.origin}/scan/public/${key}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Scan link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleOpenScanLink = async (event) => {
    const key = await ensureScanKey(event);
    if (!key) return;
    window.open(`/scan/public/${key}`, '_blank', 'noopener,noreferrer');
  };

  const handleReinstate = async () => {
    const { event } = reinstateDialog;
    if (!event || !reinstateDate) return;
    setReinstating(true);
    try {
      // datetime-local returns 'YYYY-MM-DDTHH:mm' (local). Append seconds so backend isoformat parses cleanly.
      const isoDate = new Date(reinstateDate).toISOString();
      const payload = { date: isoDate };
      if (reinstateEndDate) payload.end_date = new Date(reinstateEndDate).toISOString();
      await axios.post(`${API_URL}/api/admin/events/${event.event_id}/reinstate`, payload);
      toast.success('Event reinstated successfully');
      setReinstateDialog({ open: false, event: null });
      setReinstateDate('');
      setReinstateEndDate('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reinstate event');
    } finally {
      setReinstating(false);
    }
  };

  const handleReconcile = async (eventId) => {
    setReconcilingEvent(eventId);
    try {
      const res = await axios.post(`${API_URL}/api/admin/events/${eventId}/reconcile-tickets`);
      const d = res.data;
      if (d.changes && d.changes.length > 0) {
        const details = d.changes.map(c => `${c.ticket_type}: available ${c.available}`).join(', ');
        toast.success(`Reconciled — ${d.confirmed_tickets} confirmed tickets. ${details}`);
      } else {
        toast.success(`No changes needed — ${d.confirmed_tickets} confirmed, availability already correct`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile tickets');
    } finally {
      setReconcilingEvent(null);
    }
  };

  const getApprovalBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Rejected</Badge>;
      case 'pending':
      default:
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Pending</Badge>;
    }
  };

  const roleColors = {
    admin: 'bg-red-500/10 text-red-500 border-red-500/20',
    organizer: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    attendee: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20'
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* 2FA Required Alert */}
        {accessError && (
          <Alert className="mb-6 border-yellow-500/50 bg-yellow-500/10">
            <ShieldOff className="w-4 h-4 text-yellow-500" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-yellow-200">{accessError}</span>
              <Link to="/admin/security">
                <Button size="sm" variant="outline" className="ml-4">
                  <Lock className="w-4 h-4 mr-2" />
                  Setup 2FA
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <Shield className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">Admin Panel</h1>
                {user?.admin_role && (
                  <Badge className={`${ADMIN_ROLE_LABELS[user.admin_role]?.color || 'bg-gray-500'}`}>
                    {ADMIN_ROLE_LABELS[user.admin_role]?.label || user.admin_role}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Manage users, events, and platform settings</span>
                {has2FAEnabled ? (
                  <span className="flex items-center gap-1 text-green-500 text-xs">
                    <ShieldCheck className="w-3 h-3" /> 2FA Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-yellow-500 text-xs">
                    <ShieldOff className="w-3 h-3" /> 2FA Required
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(user?.admin_role === 'super_admin' || user?.admin_role === 'support') && (
              <Link to="/admin/support">
                <Button variant="outline" data-testid="support-dashboard-btn">
                  <Headphones className="w-4 h-4 mr-2" />
                  Support
                </Button>
              </Link>
            )}
            {user?.admin_role === 'super_admin' && (
              <Link to="/admin/analytics">
                <Button variant="outline" data-testid="admin-analytics-btn">
                  <BarChart2 className="w-4 h-4 mr-2" />
                  All Events Performance
                </Button>
              </Link>
            )}
            {(user?.admin_role === 'super_admin' || user?.admin_role === 'moderator') && (
              <Link to="/admin/moderator">
                <Button variant="outline" data-testid="moderator-dashboard-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  Moderation
                </Button>
              </Link>
            )}
            {user?.admin_role === 'super_admin' && (
              <Link to="/admin/users">
                <Button variant="outline" data-testid="manage-admins-btn">
                  <UserCog className="w-4 h-4 mr-2" />
                  Admins
                </Button>
              </Link>
            )}
            {user?.admin_role === 'super_admin' && (
              <Link to="/admin/management">
                <Button variant="outline" data-testid="admin-management-btn">
                  <Lock className="w-4 h-4 mr-2" />
                  Passwords
                </Button>
              </Link>
            )}
            {user?.admin_role === 'super_admin' && (
              <Link to="/admin/landing-control">
                <Button variant="outline" data-testid="landing-control-btn">
                  <Activity className="w-4 h-4 mr-2" />
                  Landing
                </Button>
              </Link>
            )}
            <Link to="/admin/security">
              <Button variant="outline" data-testid="security-settings-btn">
                <Shield className="w-4 h-4 mr-2" />
                Security
              </Button>
            </Link>
            <Link to="/admin/media">
              <Button variant="outline" data-testid="manage-media-btn">
                <Image className="w-4 h-4 mr-2" />
                Media
              </Button>
            </Link>
            {user?.admin_role === 'super_admin' && (
              <Link to="/admin/s3-migration">
                <Button variant="outline" data-testid="s3-migration-btn">
                  <Cloud className="w-4 h-4 mr-2" />
                  S3 Migration
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_users}</p>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <Calendar className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_events}</p>
                    <p className="text-xs text-muted-foreground">Total Events</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Clock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.pending_events}</p>
                    <p className="text-xs text-muted-foreground">Pending Approval</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.approved_events}</p>
                    <p className="text-xs text-muted-foreground">Approved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Ticket className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_tickets}</p>
                    <p className="text-xs text-muted-foreground">Tickets Sold</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10">
                    <TrendingUp className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_transactions}</p>
                    <p className="text-xs text-muted-foreground">Transactions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" data-testid="admin-tab-pending" className="relative">
              Pending Approval
              {pendingEvents.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-500 text-yellow-950 text-xs font-bold">
                  {pendingEvents.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events">All Events</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            {user?.admin_role === 'super_admin' && (
              <>
              <TabsTrigger value="activity" data-testid="admin-tab-activity" className="flex items-center gap-1">
                <Activity className="w-4 h-4" />
                User Activity
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="admin-tab-security" className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Security Log
              </TabsTrigger>
              <TabsTrigger value="tickets" data-testid="admin-tab-tickets" className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Ticket Emails
              </TabsTrigger>
              </>
            )}
            {(user?.admin_role === 'super_admin' || user?.admin_role === 'finance') && (
              <TabsTrigger value="payouts" data-testid="admin-tab-payouts" className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                Payouts
              </TabsTrigger>
            )}
            {(user?.admin_role === 'super_admin' || user?.admin_role === 'finance') && (
              <TabsTrigger value="bank-requests" data-testid="admin-tab-bank-requests" className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                Bank Requests
              </TabsTrigger>
            )}
            {user?.admin_role === 'super_admin' && (
              <TabsTrigger value="diagnostics" data-testid="admin-tab-diagnostics" className="flex items-center gap-1">
                <Search className="w-4 h-4" />
                Diagnostics
              </TabsTrigger>
            )}
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  Events Pending Approval ({pendingEvents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-24 skeleton rounded-lg" />
                    ))}
                  </div>
                ) : pendingEvents.length > 0 ? (
                  <div className="space-y-4">
                    {pendingEvents.map((event) => (
                      <div 
                        key={event.event_id} 
                        className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/5"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-20 h-14 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            <img 
                              src={getImageUrl(event.image_url)} 
                              alt={event.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold">{event.title}</h4>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(event.date), 'MMM d, yyyy • h:mm a')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {event.venue}, {event.city} • by {event.organizer_name}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid="admin-pending-organizer-info">
                              {event.organizer_display_name
                                ? <>Display: <strong>{event.organizer_display_name}</strong> · Account: {event.organizer_account_name} ({event.organizer_account_email})</>
                                : <>Account: {event.organizer_account_name} ({event.organizer_account_email})</>}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{event.category}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {event.ticket_types?.length || 0} ticket types
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a 
                            href={`/events/${event.event_id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <Button variant="outline" size="sm" data-testid={`preview-${event.event_id}`}>
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => setApprovalDialog({ open: true, event, action: 'approved' })}
                            data-testid={`approve-${event.event_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setApprovalDialog({ open: true, event, action: 'rejected' })}
                            data-testid={`reject-${event.event_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">All caught up!</h3>
                    <p className="text-muted-foreground">No events pending approval.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>All Users ({usersPagination.total_count})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 skeleton rounded-lg" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.map((user) => (
                      <div 
                        key={user.user_id} 
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {user.picture ? (
                              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                            ) : (
                              <span className="text-sm font-medium">{user.name?.charAt(0)}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                            {user.phone && <p className="text-xs text-muted-foreground">{user.phone}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleUpdateRole(user.user_id, value)}
                          >
                            <SelectTrigger className="w-[130px]" data-testid={`role-select-${user.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="attendee">Attendee</SelectItem>
                              <SelectItem value="organizer">Organizer</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => setDeleteDialog({ open: true, type: 'user', item: user })}
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {/* Pagination Controls */}
                    {usersPagination.total_pages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t" data-testid="users-pagination">
                        <p className="text-sm text-muted-foreground">
                          Showing {((usersPagination.page - 1) * usersPagination.limit) + 1}–{Math.min(usersPagination.page * usersPagination.limit, usersPagination.total_count)} of {usersPagination.total_count} users
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={usersPagination.page <= 1}
                            onClick={() => fetchData(usersPagination.page - 1)}
                            data-testid="users-prev-page"
                          >
                            Previous
                          </Button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(usersPagination.total_pages, 7) }, (_, i) => {
                              let pageNum;
                              const tp = usersPagination.total_pages;
                              const cp = usersPagination.page;
                              if (tp <= 7) {
                                pageNum = i + 1;
                              } else if (cp <= 4) {
                                pageNum = i + 1;
                              } else if (cp >= tp - 3) {
                                pageNum = tp - 6 + i;
                              } else {
                                pageNum = cp - 3 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={pageNum === cp ? 'default' : 'outline'}
                                  size="sm"
                                  className="w-9 h-9 p-0"
                                  onClick={() => fetchData(pageNum)}
                                  data-testid={`users-page-${pageNum}`}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={usersPagination.page >= usersPagination.total_pages}
                            onClick={() => fetchData(usersPagination.page + 1)}
                            data-testid="users-next-page"
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle>All Events ({events.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-20 skeleton rounded-lg" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event) => (
                      <div 
                        key={event.event_id} 
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            <img 
                              src={getImageUrl(event.image_url)} 
                              alt={event.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{event.title}</p>
                              {getApprovalBadge(event.approval_status)}
                              <Badge variant={event.is_published ? 'default' : 'secondary'}>
                                {event.is_published ? 'Published' : 'Draft'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(event.date), 'MMM d, yyyy')} • {event.organizer_name}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid="admin-event-organizer-info">
                              {event.organizer_display_name
                                ? <>Display: <strong>{event.organizer_display_name}</strong> · Account: {event.organizer_account_name} ({event.organizer_account_email})</>
                                : <>Account: {event.organizer_account_name} ({event.organizer_account_email})</>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{event.category}</Badge>
                          {event.approval_status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => setApprovalDialog({ open: true, event, action: 'approved' })}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setApprovalDialog({ open: true, event, action: 'rejected' })}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={scanKeyBusy === event.event_id}
                            onClick={() => handleCopyScanLink(event)}
                            data-testid={`copy-scan-link-${event.event_id}`}
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            {scanKeyBusy === event.event_id ? 'Generating…' : 'Scan Link'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={scanKeyBusy === event.event_id}
                            onClick={() => handleOpenScanLink(event)}
                            data-testid={`open-scan-${event.event_id}`}
                            title="Open scan page"
                          >
                            <ScanLine className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={reconcilingEvent === event.event_id}
                            onClick={() => handleReconcile(event.event_id)}
                            data-testid={`reconcile-${event.event_id}`}
                          >
                            <RotateCcw className={`w-4 h-4 mr-1 ${reconcilingEvent === event.event_id ? 'animate-spin' : ''}`} />
                            {reconcilingEvent === event.event_id ? 'Reconciling...' : 'Reconcile'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReinstateDialog({ open: true, event })}
                            data-testid={`reinstate-${event.event_id}`}
                            title={event.is_archived ? 'Reinstate this archived event' : 'Reinstate event with a new date'}
                          >
                            <Calendar className="w-4 h-4 mr-1" />
                            Reinstate
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => setDeleteDialog({ open: true, type: 'event', item: event })}
                            data-testid={`delete-event-${event.event_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Activity Tab - Super Admin Only */}
          {user?.admin_role === 'super_admin' && (
            <TabsContent value="activity">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-primary" />
                      User Activity Tracking
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search users..."
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          className="pl-9 w-[200px]"
                          data-testid="user-activity-search"
                          onKeyDown={(e) => e.key === 'Enter' && fetchUsersWithActivity()}
                        />
                      </div>
                      <Select value={userRoleFilter} onValueChange={(v) => { setUserRoleFilter(v); }}>
                        <SelectTrigger className="w-[130px]" data-testid="user-role-filter">
                          <SelectValue placeholder="All Roles" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Roles</SelectItem>
                          <SelectItem value="attendee">Attendees</SelectItem>
                          <SelectItem value="organizer">Organizers</SelectItem>
                          <SelectItem value="admin">Admins</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="icon" onClick={fetchUsersWithActivity} disabled={activityLoading} data-testid="refresh-activity-btn">
                        <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {activityLoading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-20 skeleton rounded-lg" />
                      ))}
                    </div>
                  ) : usersWithActivity.length > 0 ? (
                    <div className="space-y-3">
                      {usersWithActivity.map((targetUser) => (
                        <div 
                          key={targetUser.user_id} 
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer"
                          onClick={() => handleViewUserActivity(targetUser)}
                          data-testid={`user-activity-row-${targetUser.user_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              {targetUser.picture ? (
                                <img src={targetUser.picture} alt={targetUser.name} className="w-10 h-10 rounded-full" />
                              ) : (
                                <span className="text-sm font-medium">{targetUser.name?.charAt(0)}</span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{targetUser.name}</p>
                              <p className="text-sm text-muted-foreground">{targetUser.email}</p>
                              {targetUser.phone && <p className="text-xs text-muted-foreground">{targetUser.phone}</p>}
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={roleColors[targetUser.role]} variant="outline">
                                  {targetUser.role}
                                </Badge>
                                {targetUser.admin_role && (
                                  <Badge className={ADMIN_ROLE_LABELS[targetUser.admin_role]?.color}>
                                    {ADMIN_ROLE_LABELS[targetUser.admin_role]?.label}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            {/* Activity Summary Stats */}
                            <div className="grid grid-cols-5 gap-3 text-center">
                              <div title="Logins">
                                <p className="text-lg font-semibold text-blue-500">{targetUser.activity_summary?.logins || 0}</p>
                                <p className="text-xs text-muted-foreground">Logins</p>
                              </div>
                              <div title="Tickets">
                                <p className="text-lg font-semibold text-purple-500">{targetUser.activity_summary?.tickets_purchased || 0}</p>
                                <p className="text-xs text-muted-foreground">Tickets</p>
                              </div>
                              <div title="Events">
                                <p className="text-lg font-semibold text-green-500">{targetUser.activity_summary?.events_created || 0}</p>
                                <p className="text-xs text-muted-foreground">Events</p>
                              </div>
                              <div title="Complaints">
                                <p className="text-lg font-semibold text-yellow-500">{targetUser.activity_summary?.complaints_filed || 0}</p>
                                <p className="text-xs text-muted-foreground">Complaints</p>
                              </div>
                              <div title="Total">
                                <p className="text-lg font-semibold">{targetUser.activity_summary?.total || 0}</p>
                                <p className="text-xs text-muted-foreground">Total</p>
                              </div>
                            </div>
                            <div className="text-right min-w-[100px]">
                              <p className="text-xs text-muted-foreground">Last activity</p>
                              <p className="text-sm">
                                {targetUser.last_activity ? formatDistanceToNow(new Date(targetUser.last_activity), { addSuffix: true }) : 'Never'}
                              </p>
                            </div>
                            <Button variant="outline" size="sm" data-testid={`view-activity-${targetUser.user_id}`} onClick={(e) => { e.stopPropagation(); handleViewUserActivity(targetUser); }}>
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No users found</h3>
                      <p className="text-muted-foreground">
                        {userSearchQuery || userRoleFilter !== 'all' 
                          ? 'Try adjusting your search or filter criteria.'
                          : 'No users match your criteria.'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Security Log Tab */}
          {user?.admin_role === 'super_admin' && (
            <TabsContent value="security">
              <SecurityLog />
            </TabsContent>
          )}

          {/* Ticket Emails Tab */}
          {user?.admin_role === 'super_admin' && (
            <TabsContent value="tickets">
              <TicketResendManager />
            </TabsContent>
          )}

          {/* Payouts Tab */}
          {(user?.admin_role === 'super_admin' || user?.admin_role === 'finance') && (
            <TabsContent value="payouts">
              <AdminPayouts />
            </TabsContent>
          )}

          {/* Bank Account Requests Tab — super_admin + finance */}
          {(user?.admin_role === 'super_admin' || user?.admin_role === 'finance') && (
            <TabsContent value="bank-requests">
              <BankAccountRequests />
            </TabsContent>
          )}

          {/* Diagnostics Tab */}
          {user?.admin_role === 'super_admin' && (
            <TabsContent value="diagnostics">
              <AdminDiagnostics initialSubtab={diagSubtab} subtabKey={diagSubtab} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* User Activity View Dialog */}
      <UserActivityView 
        user={activityUser} 
        open={activityViewOpen} 
        onClose={() => { setActivityViewOpen(false); setActivityUser(null); }} 
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteDialog.type}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog.open} onOpenChange={(open) => !open && setApprovalDialog({ ...approvalDialog, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {approvalDialog.action === 'approved' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              {approvalDialog.action === 'approved' ? 'Approve Event' : 'Reject Event'}
            </DialogTitle>
            <DialogDescription>
              {approvalDialog.action === 'approved' 
                ? `Are you sure you want to approve "${approvalDialog.event?.title}"? This will make it visible to all users.`
                : `Are you sure you want to reject "${approvalDialog.event?.title}"? Please provide a reason for rejection.`
              }
            </DialogDescription>
          </DialogHeader>
          {approvalDialog.action === 'rejected' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason</label>
              <Textarea
                placeholder="Please provide a reason for rejecting this event..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                data-testid="rejection-reason-textarea"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog({ ...approvalDialog, open: false })}>
              Cancel
            </Button>
            <Button
              variant={approvalDialog.action === 'approved' ? 'default' : 'destructive'}
              onClick={handleApproval}
              disabled={approvalDialog.action === 'rejected' && !rejectionReason.trim()}
              data-testid="confirm-approval-btn"
            >
              {approvalDialog.action === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate Event Dialog */}
      <Dialog
        open={reinstateDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setReinstateDialog({ open: false, event: null });
            setReinstateDate('');
            setReinstateEndDate('');
          }
        }}
      >
        <DialogContent data-testid="reinstate-dialog">
          <DialogHeader>
            <DialogTitle>Reinstate Event</DialogTitle>
            <DialogDescription>
              {reinstateDialog.event?.title}. Set a new event date — the event will be re-published and become purchaseable immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reinstate-date">New event date &amp; time *</Label>
              <Input
                id="reinstate-date"
                type="datetime-local"
                value={reinstateDate}
                onChange={(e) => setReinstateDate(e.target.value)}
                data-testid="reinstate-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reinstate-end-date">New end date &amp; time (optional)</Label>
              <Input
                id="reinstate-end-date"
                type="datetime-local"
                value={reinstateEndDate}
                onChange={(e) => setReinstateEndDate(e.target.value)}
                data-testid="reinstate-end-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReinstateDialog({ open: false, event: null });
                setReinstateDate('');
                setReinstateEndDate('');
              }}
              data-testid="reinstate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReinstate}
              disabled={!reinstateDate || reinstating}
              data-testid="reinstate-confirm-btn"
            >
              {reinstating ? 'Reinstating…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
